import { FC, useEffect } from 'react';
import { observer } from 'mobx-react';

import { Banner, Headline, Layout, Page } from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';

import { PageSpinner } from '../../common';
import { AppClientListPageStore } from '../stores/app-client-list-page.store';
import { AppClientTable } from './app-client-table';
import { AppPageHeader } from './app-page-header';
import { SecretManager } from './secret-manager';

export interface AppClientListPageProps {
    id: number;
}

export const AppClientListPage: FC<AppClientListPageProps> = provide({
    singletons: [AppClientListPageStore],
})(
    observer(({ id }) => {
        const [store] = useDependencies(AppClientListPageStore);

        useEffect(() => {
            store.load(id);
        }, [store, id]);

        if (store.loading) {
            return <PageSpinner />;
        }

        const pageHeader = (
            <AppPageHeader
                id={id}
                publicId={store.publicId}
                appName={store.appName}
                deleted={store.deleted}
                errorBannerVisible={store.errorBannerVisible}
            />
        );

        if (store.errorBannerVisible) {
            return (
                <Page header={pageHeader}>
                    <Layout>
                        <Banner
                            status="critical"
                            icon
                            title="An error occurred while loading this page."
                        />
                    </Layout>
                </Page>
            );
        }

        return (
            <Page header={pageHeader}>
                <Layout>
                    <Headline>App Connections</Headline>
                    <AppClientTable resourceOwnerTerm={store.apiGroup!.resourceOwnerTerm} />
                    {store.selectedClient && <SecretManager key={store.selectedClient.clientId} />}
                </Layout>
            </Page>
        );
    }),
);
AppClientListPage.displayName = 'AppClientListPage';
